import { useCallback, useEffect, useMemo } from 'react';

import { type DocumentNode, gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { EyeIcon, EyeOffIcon } from 'lucide-react';

import { DIMENSIONS } from '@modules/_shared/components/report-editor/constants';
import { reportStepAnchor } from '@modules/_shared/components/report-editor/preview/previewAnchors';
import { useReportContext } from '@modules/_shared/components/report-editor/ReportEditorContext';
import type { StepType } from '@modules/_shared/components/report-editor/ReportEditorStepper';
import { useSingleFlight } from '@modules/_shared/hooks/useSingleFlight';
import { tourAnchor } from '@modules/product-updates/tours/tourTypes';

const DUMMY_MUTATION = gql/* GraphQL */ `
  mutation DummyMutation {
    __typename
  }
`;

const hasToggleField = (
  step: StepType,
): step is StepType & { databaseToggleField: string } =>
  'databaseToggleField' in step;

const isStepActive = (step: StepType): boolean =>
  'active' in step && Boolean(step.active);

const getStepIcon = (step: StepType, isActive: boolean) => {
  if (isActive && 'activeIcon' in step && step.activeIcon) {
    return step.activeIcon;
  }
  return step.icon;
};

type StepperListItemProps = {
  item: StepType;
  index: number;
  isExpanded?: boolean;
  isActive: boolean;
  handleStepSelect?: (index: number) => void;
  onStepSelect?: (index: number, item: StepType) => void;
};

const createMutation = (
  tableName: string = 'lots',
  returnFields: string[],
): DocumentNode => {
  const returnFieldsString = returnFields.join('\n          ');

  const mutationStr = `
    mutation UpdateStepVisibility($id: uuid!, $input: ${tableName}_set_input!) {
      update_${tableName}_by_pk(pk_columns: {id: $id}, _set: $input) {
        id
        updated_at
        ${returnFieldsString}
      }
    }
  `;

  return gql(mutationStr);
};

const ReportEditorStepperListItem = ({
  item,
  index,
  isExpanded,
  isActive,
  handleStepSelect,
  onStepSelect,
}: StepperListItemProps) => {
  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const {
    data,
    setCurrentStepByIndex,
    setContentPanelExpanded,
    includeFields,
    toggleStepVisibilityRef,
    setError,
  } = useReportContext();
  const tableName = data.__typename?.toLowerCase();

  const hasMutation = hasToggleField(item);

  const visibilityMutation = useMemo(
    () =>
      hasMutation
        ? createMutation(tableName, includeFields || [])
        : DUMMY_MUTATION,
    [hasMutation, tableName, includeFields],
  );

  // The preview reads server data, so refresh only after visibility is saved.
  const [updateStepVisibility, { loading: savingVisibility }] =
    useMutation(visibilityMutation);
  const { run: runVisibilitySave } = useSingleFlight();

  const handleClick = useCallback(() => {
    if ('onClick' in item && typeof item.onClick === 'function') {
      item.onClick();
      return;
    }

    // Ensure the content panel is expanded when clicking on a step
    setContentPanelExpanded(true);

    // Call custom handler if provided
    if (onStepSelect) {
      onStepSelect(index, item);
      return;
    }

    // Default handlers
    handleStepSelect?.(index);
    setCurrentStepByIndex?.(index);
  }, [
    handleStepSelect,
    index,
    item,
    onStepSelect,
    setCurrentStepByIndex,
    setContentPanelExpanded,
  ]);

  const handleToggleVisibility = useCallback(
    async (e?: React.MouseEvent) => {
      e?.stopPropagation();

      if (!hasMutation) {
        return;
      }

      await runVisibilitySave(async () => {
        try {
          await updateStepVisibility({
            variables: {
              id: data?.id,
              input: {
                [item.databaseToggleField]: !isStepActive(item),
              },
            },
          });
        } catch (error) {
          setError(error instanceof Error ? error : new Error(String(error)));
        }
      });
    },
    [
      data?.id,
      updateStepVisibility,
      hasMutation,
      item,
      runVisibilitySave,
      setError,
    ],
  );

  // Register toggle callback in context ref so the disabled overlay CTA can trigger it
  useEffect(() => {
    const prevCallback = toggleStepVisibilityRef.current;
    toggleStepVisibilityRef.current = (targetIndex: number) => {
      if (targetIndex === index) {
        handleToggleVisibility();
      } else if (prevCallback) {
        prevCallback(targetIndex);
      }
    };

    return () => {
      toggleStepVisibilityRef.current = prevCallback;
    };
  }, [index, handleToggleVisibility, toggleStepVisibilityRef]);

  const getListItemStyles = useMemo(
    () => ({
      padding: 0,
      '&:hover': { bgcolor: 'grey.200' },
      backgroundColor: isActive ? 'grey.300' : 'background.paper',
      ...(item.position === 'footer' && {
        marginTop: 'auto',
      }),
    }),
    [isActive, item],
  );

  return (
    <ListItem
      {...(item.pageId == null
        ? {}
        : tourAnchor(reportStepAnchor(item.pageId)))}
      disablePadding
      onClick={handleClick}
      sx={getListItemStyles}
    >
      <ListItemButton
        disableRipple
        sx={{
          minHeight: DIMENSIONS.STEP_HEIGHT,
          p: 0,
        }}
      >
        <ListItemIcon
          sx={{
            color: 'grey.700',
            minWidth: 0,
            ml: 2.5,
            mr: 2.5,
          }}
        >
          {getStepIcon(item, isActive)}
        </ListItemIcon>
        {(isExpanded || isMobile) && (
          <>
            <ListItemText primary={item.title} sx={{ whiteSpace: 'wrap' }} />
            {hasMutation && (
              <IconButton
                onClick={handleToggleVisibility}
                loading={savingVisibility}
                size="small"
                sx={{ mr: 1 }}
              >
                {isStepActive(item) ? (
                  <EyeIcon
                    size={DIMENSIONS.ICON_SIZE}
                    color={'var(--mui-palette-text-primary)'}
                  />
                ) : (
                  <EyeOffIcon
                    size={DIMENSIONS.ICON_SIZE}
                    color={'var(--mui-palette-grey-500)'}
                  />
                )}
              </IconButton>
            )}
          </>
        )}
      </ListItemButton>
    </ListItem>
  );
};

export default ReportEditorStepperListItem;
